/**
 * LeftShield Portal - Dependencies Page
 * SCA vulnerabilities, container image risks, and dependency analysis.
 *
 * Route: /dependencies | Nav: Security > Dependencies
 *
 * Migrated onto the List & Grid Standard — see `.github/context/list-grid-standard.context.md`,
 * `.github/context/list-grid-standard-behaviours.context.md`, and the reference migration
 * `src/pages/ApiKeysPage.tsx`. Two grids (Vulnerabilities / Container Images) share a single
 * `useListUrlState` instance since only one tab is visible at a time (§19) — the default sort
 * is tab-scoped (`DEFAULT_SORT_BY_TAB`), recomputed per render off `selectedTab`, so an implicit
 * (no `?sort=` in the URL) sort falls back to the right column for whichever tab is active.
 *
 * Chevrons route to the GUID-based detail pages (#12746 Wave 2) — `/security/vulnerabilities/:referenceId`
 * and `/security/findings/:referenceId` — replacing the former local-state `DetailDrawer` instances.
 */
import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Button,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dropdown,
  Option,
  tokens,
  makeStyles,
  SelectTabEvent,
  SelectTabData,
} from '@fluentui/react-components';
import {
  BoxRegular,
  WarningRegular,
  BugRegular,
  ShieldErrorRegular,
  ChevronRightRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { vulnerabilitiesApi, findingsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import type { Vulnerability, Finding, Severity } from '../types';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

type TabValue = 'vulnerabilities' | 'container-images';

const crumbs: Crumb[] = [{ label: 'Dependencies' }];

/** Tab-scoped sort fallback (§19) — first sortable column on each grid, ascending. */
const DEFAULT_SORT_BY_TAB: Record<TabValue, string> = {
  vulnerabilities: 'identifier:asc',
  'container-images': 'title:asc',
};

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  tabsRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
});

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const getStatusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status?.toLowerCase()) {
    case 'new':
    case 'open':
      return 'danger';
    case 'triaged':
    case 'inremediation':
    case 'inprogress':
      return 'warning';
    case 'resolved':
    case 'closed':
      return 'success';
    default:
      return 'informative';
  }
};

/** The vulnerabilities endpoint is already server-paginated — `fetchPage` just forwards
 *  page/pageSize; `useAdaptiveGridData`'s probe (pageSize = threshold) decides client vs
 *  server mode from the real `totalCount`. Search has no server-side param, so it only
 *  applies in client mode (via `searchFields`) — see the module doc comment for the
 *  known limitation this leaves in server mode on a very large result set. */
async function fetchVulnerabilitiesPage(
  severity: Severity | '',
  params: ListQueryParams
): Promise<PagedResult<Vulnerability>> {
  return vulnerabilitiesApi.getAll({ severity: severity || undefined }, params.page, params.pageSize);
}

async function fetchContainerFindingsPage(
  severity: Severity | '',
  params: ListQueryParams
): Promise<PagedResult<Finding>> {
  return findingsApi.getAll(
    { type: 'DependencyRisk', severity: severity || undefined },
    params.page,
    params.pageSize
  );
}

export const DependenciesPage: FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<TabValue>('vulnerabilities');

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({
    defaultSort: DEFAULT_SORT_BY_TAB[selectedTab],
  });

  // Severity is a single-value scope filter (a toolbar dropdown, not a per-column filter
  // panel) — encoded into the shared `filters` URL param as `severity:eq:<value>` (§8),
  // mirroring ApiKeysPage's boolean `includeRevoked` toggle.
  const severityMatch = /^severity:eq:(.+)$/.exec(state.filters);
  const severityFilter = (severityMatch?.[1] as Severity | undefined) ?? '';
  const handleSeverityChange = (value: Severity | '') =>
    setFilters(value ? `severity:eq:${value}` : '');

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const vulnGrid = useAdaptiveGridData<Vulnerability>({
    queryKeyBase: ['dependencies', 'vulnerabilities', severityFilter],
    fetchPage: (params) => fetchVulnerabilitiesPage(severityFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['identifier', 'title'],
    sortState,
    enabled: selectedTab === 'vulnerabilities',
  });

  const containerGrid = useAdaptiveGridData<Finding>({
    queryKeyBase: ['dependencies', 'containerFindings', severityFilter],
    fetchPage: (params) => fetchContainerFindingsPage(severityFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['title', 'repository'],
    sortState,
    enabled: selectedTab === 'container-images',
  });

  const activeGrid = selectedTab === 'vulnerabilities' ? vulnGrid : containerGrid;

  // Out-of-range clamp (§9) — clamp against whichever tab is visible; the inactive tab's
  // grid is disabled and its stale totalPages must not drive the clamp.
  useEffect(() => {
    if (!activeGrid.isLoading && state.page > activeGrid.totalPages) {
      setPage(activeGrid.totalPages);
    }
  }, [activeGrid.isLoading, activeGrid.totalPages, state.page, setPage]);

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  // Summary stats reuse the vulnerabilities grid's own query (no extra fetch). "Total
  // Vulnerabilities" reflects the server's real `totalCount`; the Critical/High/KEV badge
  // counts are computed from the current page only — a pre-existing limitation carried
  // over from the pre-migration page (which computed them from a single unpaged fetch),
  // just now scoped to the 25-row page instead of a 50-row fetch. Flagged, not fixed here.
  const totalVulnerabilities = vulnGrid.totalCount;
  const criticalCount = vulnGrid.items.filter((v) => v.severity === 'Critical').length;
  const highCount = vulnGrid.items.filter((v) => v.severity === 'High').length;
  const kevCount = vulnGrid.items.filter((v) => v.isKnownExploited).length;

  const severityFilterDropdown = (
    <Dropdown
      key="severity-filter"
      placeholder="Severity"
      value={severityFilter}
      onOptionSelect={(_, data) => handleSeverityChange((data.optionValue || '') as Severity | '')}
      style={{ minWidth: '150px' }}
    >
      <Option value="">All Severities</Option>
      <Option value="Critical">Critical</Option>
      <Option value="High">High</Option>
      <Option value="Medium">Medium</Option>
      <Option value="Low">Low</Option>
    </Dropdown>
  );

  let vulnBody: ReactNode;
  if (vulnGrid.isLoading) {
    vulnBody = <LoadingSkeleton rows={6} columns={8} />;
  } else if (vulnGrid.isError) {
    vulnBody = <ErrorState message="Unable to load vulnerabilities." onRetry={vulnGrid.refetch} />;
  } else if (vulnGrid.items.length === 0) {
    vulnBody = (
      <EmptyState
        icon={<BugRegular />}
        title="No Vulnerabilities Found"
        description="No scans executed yet. Run a dependency scan to detect issues."
      />
    );
  } else {
    vulnBody = (
      <ListLoadingOverlay busy={vulnGrid.isFetching && !vulnGrid.isLoading}>
        <Table aria-label="Vulnerabilities">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Severity</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'identifier' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('identifier')}
                onClick={() => toggleSort('identifier')}
              >
                Identifier
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'title' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('title')}
                onClick={() => toggleSort('title')}
                style={{ width: '100%' }}
              >
                Title
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'affectedPackageCount'
                    ? sortDir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
                sortIcon={sortIcon('affectedPackageCount')}
                onClick={() => toggleSort('affectedPackageCount')}
              >
                Affected Packages
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'epssScore' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('epssScore')}
                onClick={() => toggleSort('epssScore')}
              >
                EPSS Score
              </TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Exploited</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {vulnGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.severity)}>
                    {item.severity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontFamily: 'monospace', fontWeight: 500 }}>{item.identifier}</Body1>
                </TableCell>
                <TableCell>
                  <Body1>{item.title}</Body1>
                </TableCell>
                <TableCell>
                  <Body2>{item.affectedPackageCount}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{item.epssScore != null ? `${(item.epssScore * 100).toFixed(2)}%` : '-'}</Body2>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={getStatusColor(item.status)}>
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {item.isKnownExploited ? (
                    <Badge appearance="filled" color="danger">
                      KEV
                    </Badge>
                  ) : (
                    <Body2>-</Body2>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    aria-label="View vulnerability details"
                    onClick={() => navigate(`/security/vulnerabilities/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  let containerBody: ReactNode;
  if (containerGrid.isLoading) {
    containerBody = <LoadingSkeleton rows={6} columns={5} />;
  } else if (containerGrid.isError) {
    containerBody = <ErrorState message="Unable to load container findings." onRetry={containerGrid.refetch} />;
  } else if (containerGrid.items.length === 0) {
    containerBody = (
      <EmptyState
        icon={<BoxRegular />}
        title="No Container Image Findings"
        description="Run a container scan to detect dependency risks."
      />
    );
  } else {
    containerBody = (
      <ListLoadingOverlay busy={containerGrid.isFetching && !containerGrid.isLoading}>
        <Table aria-label="Container image findings">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Severity</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'title' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('title')}
                onClick={() => toggleSort('title')}
                style={{ width: '100%' }}
              >
                Title
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'repository' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('repository')}
                onClick={() => toggleSort('repository')}
              >
                Repo
              </TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {containerGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.severity)}>
                    {item.severity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <Body1 style={{ fontWeight: 500 }}>{item.title}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      {item.controlId} - {item.frameworkCode}
                    </Body2>
                  </div>
                </TableCell>
                <TableCell>
                  <Body2 style={{ fontFamily: 'monospace' }}>{item.repository ?? '-'}</Body2>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={getStatusColor(item.status)}>
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    aria-label="View container finding details"
                    onClick={() => navigate(`/security/findings/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      {/* Summary Cards — non-list page content, sits outside the scrolling list frame. */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<BoxRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
            header={<Body2>Total Vulnerabilities</Body2>}
            description={<Title2>{totalVulnerabilities}</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={<ShieldErrorRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />}
            header={<Body2>Critical</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>{criticalCount}</Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<BugRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground2 }} />}
            header={<Body2>Known Exploited (KEV)</Body2>}
            description={
              <Title2
                style={{
                  color: kevCount > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground1,
                }}
              >
                {kevCount}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<WarningRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground2 }} />}
            header={<Body2>High Severity</Body2>}
            description={
              <Title2
                style={{
                  color: highCount > 0 ? tokens.colorPaletteRedForeground2 : tokens.colorNeutralForeground1,
                }}
              >
                {highCount}
              </Title2>
            }
          />
        </Card>
      </div>

      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <>
              <MasterDetailHeader crumbs={crumbs} busy={activeGrid.isFetching && !activeGrid.isLoading} />
              <div className={styles.tabsRow}>
                <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
                  <Tab value="vulnerabilities" icon={<BugRegular />}>
                    Vulnerabilities
                  </Tab>
                  <Tab value="container-images" icon={<BoxRegular />}>
                    Container Images
                  </Tab>
                </TabList>
              </div>
            </>
          }
          toolbar={
            <ListToolbar
              fkFilters={[severityFilterDropdown]}
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search…"
              onRefresh={activeGrid.refetch}
              isRefreshing={activeGrid.isFetching}
              testIdPrefix="dependencies"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={activeGrid.totalPages}
              hasPreviousPage={activeGrid.hasPreviousPage}
              hasNextPage={activeGrid.hasNextPage}
              totalCount={activeGrid.totalCount}
              onPageChange={setPage}
              testId="dependencies-pager"
            />
          }
        >
          {selectedTab === 'vulnerabilities' ? vulnBody : containerBody}
        </ListPageLayout>
      </div>

    </div>
  );
};
